import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function WorkGallery({ items = [] }) {
  const [open, setOpen] = useState(null); // index into `filtered` or null
  const STEP = 15;
  const [visible, setVisible] = useState(STEP);
  const [filter, setFilter] = useState("All");

  const groups = useMemo(() => {
    const seen = [];
    items.forEach((it) => { if (it.group && !seen.includes(it.group)) seen.push(it.group); });
    return seen;
  }, [items]);

  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((it) => it.group === filter)),
    [items, filter]
  );

  const pickFilter = (f) => { setFilter(f); setVisible(STEP); setOpen(null); };

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback((e) => { e?.stopPropagation(); setOpen((i) => (i - 1 + filtered.length) % filtered.length); }, [filtered.length]);
  const next = useCallback((e) => { e?.stopPropagation(); setOpen((i) => (i + 1) % filtered.length); }, [filtered.length]);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  if (!items.length) return null;

  const showHeaders = filter === "All";
  const count = (g) => (g === "All" ? items.length : items.filter((it) => it.group === g).length);

  return (
    <>
      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2.5 mb-8" data-testid="work-filters">
        {["All", ...groups].map((g) => (
          <button
            key={g}
            data-testid={`work-filter-${g}`}
            onClick={() => pickFilter(g)}
            className={`text-xs font-mono uppercase tracking-wider px-4 py-2 rounded-full border transition-all inline-flex items-center gap-1.5 ${
              filter === g ? "bg-cyan text-void border-cyan" : "border-white/15 text-white/55 hover:border-cyan/50 hover:text-white/80"
            }`}
          >
            {g}
            <span className={`text-[0.6rem] ${filter === g ? "text-void/70" : "text-white/35"}`}>{count(g)}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5" data-testid="work-gallery-grid">
        {(() => {
          const shown = filtered.slice(0, visible);
          let last = null;
          const nodes = [];
          shown.forEach((it, i) => {
            if (showHeaders && it.group && it.group !== last) {
              last = it.group;
              nodes.push(
                <div key={`h-${it.group}`} className="col-span-2 md:col-span-3 mt-6 first:mt-0" data-testid={`work-group-${it.group}`}>
                  <h3 className="font-display text-2xl md:text-3xl text-white/90">{it.group}</h3>
                  <div className="h-px bg-gradient-to-r from-cyan/50 to-transparent mt-2" />
                </div>
              );
            }
            nodes.push(
              <motion.button
                key={`${filter}-${i}`}
                type="button"
                data-testid={`work-tile-${i}`}
                onClick={() => setOpen(i)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
                className="group relative rounded-2xl overflow-hidden border border-white/10 card-glow text-left"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={it.src} alt={it.caption} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.1s] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-void via-void/10 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white/85 text-sm font-mono leading-snug">{it.caption}</p>
                </div>
              </motion.button>
            );
          });
          return nodes;
        })()}
      </div>

      {visible < filtered.length && (
        <div className="flex justify-center mt-10">
          <button
            onClick={() => setVisible((v) => Math.min(v + STEP, filtered.length))}
            data-testid="work-load-more"
            className="btn-pill border border-cyan/40 text-cyan hover:bg-cyan/10 px-7 py-3 transition-colors font-mono text-sm"
          >
            Load more — {filtered.length - visible} more
          </button>
        </div>
      )}

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[120] bg-void/92 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            data-testid="work-lightbox"
          >
            <button onClick={close} data-testid="work-lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
              <X size={20} />
            </button>
            {filtered.length > 1 && (
              <>
                <button onClick={prev} data-testid="work-lightbox-prev"
                  className="absolute left-3 md:left-8 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                  <ChevronLeft size={22} />
                </button>
                <button onClick={next} data-testid="work-lightbox-next"
                  className="absolute right-3 md:right-8 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.figure
              key={open}
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full flex flex-col items-center"
            >
              <img src={filtered[open].src} alt={filtered[open].caption}
                className="max-h-[75vh] w-auto rounded-xl border border-white/15 object-contain" />
              <figcaption className="text-white/70 text-sm font-mono mt-4 text-center">
                {filtered[open].caption} <span className="text-white/30">· {open + 1}/{filtered.length}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
